import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import useSWR from 'swr'
import Link from '@/routing/Link'
import type { FeaturedTwinData } from '@/types/routes/home'
import type { FeaturedTwin, FeaturedTwinDetail } from '@/lib/api'
import { twinsApi } from '@/lib/api'
import { HomeCarouselArrowButton } from './HomeCarouselArrowButton'
import { HomeCarouselCardFrame } from './HomeCarouselCardFrame'
import { HomeCardMetaChip } from './HomeCardMetaChip'
import { wrapIndex, getOffset, getSlotTarget, getZIndex } from './carousel-utils'
import { CoverFlowSkeleton } from './CoverFlowSkeleton'
import { Text } from '@/components/ui/Text'
import { Heading } from '@/components/ui/Heading'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { buildBlurbLine, buildLocationLine, inferAudienceType } from '@/lib/utils/twin-blurb'

const FALLBACK_IMAGE = '/images/twin-fallback.png'

function hasInlineBlurbData(twin: FeaturedTwin): boolean {
  const demographics = twin.demographics ?? {}
  if (Object.keys(demographics).length > 0) {
    return true
  }
  if (twin.personaTagline) {
    return true
  }
  return false
}

function useTwinBlurb(twin: FeaturedTwin): string | null {
  const shouldFetchDetail =
    Boolean(twin.populationId && twin.personaId) && !hasInlineBlurbData(twin)

  const { data: fullTwin } = useSWR<FeaturedTwinDetail>(
    shouldFetchDetail
      ? `twin-blurb-${twin.populationId}-${twin.personaId}`
      : null,
    () => twinsApi.getFeaturedTwin(twin.populationId!, twin.personaId),
    { revalidateOnFocus: false },
  )

  const source = fullTwin ?? twin
  const demographics = (source.demographics ?? {}) as Record<string, string>
  if (Object.keys(demographics).length > 0) {
    const audienceType = inferAudienceType(demographics)
    const blurb = buildBlurbLine(demographics, audienceType)
    const location = buildLocationLine(demographics)
    if (blurb && location) return `${blurb} · ${location}`
    if (blurb) return blurb
    if (location) return location
  }

  return source.personaTagline ?? null
}

interface TwinCoverFlowProps {
  twins: FeaturedTwinData[]
  isLoading: boolean
  activeIndex: number
  onActiveIndexChange: (index: number) => void
}

function TwinCard({
  twin,
  isActive,
  onClick,
}: {
  twin: FeaturedTwinData
  isActive: boolean
  onClick: () => void
}) {
  const blurb = useTwinBlurb(twin)
  const tagNames = twin.audienceTagNames ?? []
  return (
    <HomeCarouselCardFrame
      imageUrl={twin.imageUrl || FALLBACK_IMAGE}
      isActive={isActive}
      onClick={onClick}
      ariaLabel={twin.personaName}
      topLeft={
        twin.populationName ? (
          <HomeCardMetaChip label={twin.populationName} />
        ) : undefined
      }
      bottomContent={
        <div className="space-y-1 text-white">
          <Text className="font-display text-[18px] leading-[1.25] tracking-[-0.24px] text-white">
            {twin.personaName}
          </Text>
          {blurb ? (
            <Text className="line-clamp-2 text-[14px] leading-[1.4] text-white/85">{blurb}</Text>
          ) : null}
          {tagNames.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-0.5">
              {tagNames.map((tn) => (
                <span
                  key={tn}
                  className="inline-flex items-center rounded-full bg-white/20 px-2 py-0.5 text-[11px] leading-tight text-white/90 backdrop-blur-sm"
                >
                  {tn}
                </span>
              ))}
            </div>
          )}
        </div>
      }
    />
  )
}

const PLACEHOLDER_SLOTS = [
  { x: -244, y: 0, scale: 0.884, rotate: -4, zIndex: 10 },
  { x: 0, y: -36, scale: 1, rotate: 0, zIndex: 20 },
  { x: 244, y: 0, scale: 0.884, rotate: 4, zIndex: 10 },
]

function TwinCoverFlowEmpty() {
  return (
    <div className="flex w-full justify-center px-2 pb-2 pt-10">
      <div className="relative flex w-[700px] items-center justify-center">
        <div className="relative h-[340px] w-full pointer-events-none">
          {PLACEHOLDER_SLOTS.map((slot, i) => (
            <div
              key={i}
              className="absolute bottom-3 left-1/2"
              style={{
                marginLeft: -138,
                zIndex: slot.zIndex,
                transform: `translateX(${slot.x}px) translateY(${slot.y}px) scale(${slot.scale}) rotate(${slot.rotate}deg)`,
              }}
            >
              <div
                className="relative h-[300px] w-[276px] rounded-lg"
                style={{
                  background: 'var(--home-card-placeholder-gradient)',
                  boxShadow: 'var(--home-card-shadow)',
                }}
              />
            </div>
          ))}
        </div>

        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-4 text-center pb-20">
          <Heading level={4} className="font-sans text-[18px] font-medium text-foreground">
            No featured twins yet
          </Heading>
          <Button variant="default" size="default" asChild className="rounded-[6px] bg-primary text-base text-primary-foreground hover:bg-primary/90">
            <Link href="/audiences">
              Browse audience library
              <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  )
}

export function TwinCoverFlow({
  twins,
  isLoading,
  activeIndex,
  onActiveIndexChange,
}: TwinCoverFlowProps) {
  const goLeft = () => onActiveIndexChange(wrapIndex(activeIndex - 1, twins.length))
  const goRight = () => onActiveIndexChange(wrapIndex(activeIndex + 1, twins.length))

  if (isLoading) {
    return <CoverFlowSkeleton />
  }

  if (twins.length === 0) {
    return <TwinCoverFlowEmpty />
  }

  return (
    <div className="flex w-full justify-center px-2 pb-2 pt-10">
      <div className="relative flex w-[700px] items-center justify-center">
        {twins.length > 1 ? (
          <HomeCarouselArrowButton
            direction="left"
            onClick={goLeft}
            ariaLabel="Previous twin"
            className="absolute left-0 top-1/2 z-30 -translate-x-1/2 -translate-y-1/2"
          />
        ) : null}

        {/* CoverFlow stage: fixed height, cards slide via absolute positioning */}
        <div className="relative h-[300px] w-full">
          {twins.map((twin, idx) => {
            const offset = getOffset(idx, activeIndex, twins.length)
            const target = getSlotTarget(offset)
            const isVisible = Math.abs(offset) <= 1

            const keyId =
              typeof twin.personaId === 'string' &&
              twin.personaId.length > 0 &&
              twin.personaId !== 'null' &&
              twin.personaId !== 'undefined'
                ? twin.personaId
                : `twin-${idx}`

            return (
              <motion.div
                key={keyId}
                initial={false}
                className="absolute bottom-0 left-1/2"
                style={{
                  marginLeft: -138,
                  zIndex: getZIndex(offset),
                  pointerEvents: isVisible ? 'auto' : 'none',
                }}
                animate={target}
                transition={{
                  type: 'spring',
                  stiffness: 280,
                  damping: 28,
                  mass: 0.9,
                }}
              >
                <TwinCard
                  twin={twin}
                  isActive={offset === 0}
                  onClick={() => onActiveIndexChange(idx)}
                />
              </motion.div>
            )
          })}
        </div>

        {twins.length > 1 ? (
          <HomeCarouselArrowButton
            direction="right"
            onClick={goRight}
            ariaLabel="Next twin"
            className="absolute right-0 top-1/2 z-30 translate-x-1/2 -translate-y-1/2"
          />
        ) : null}
      </div>
    </div>
  )
}
